import { passOptics, passWindow } from '../utils/observations.js';
import { tierColor } from '../utils/passTiers.js';

// Where-to-aim panel for the focused pass, the actionable "point your eyes/
// scope here" companion to the all-sky plot. Everything is derived from the
// pass record (no fetch, no backend): where it appears, peaks, and departs in
// azimuth, how fast it moves, and how to track it.
//
// Deliberately NO RA/Dec: a satellite doesn't track the stars, so a fixed
// equatorial coordinate can't point you at one, alt/az + slew is the honest,
// usable guidance for a moving pass.

const C16 = ['N','NNE','NE','ENE','E','ESE','SE','SSE','S','SSW','SW','WSW','W','WNW','NW','NNW'];
const compass = az => az == null ? '-' : C16[Math.round(((az % 360) / 22.5)) % 16];
const MONO = 'var(--mono)';

// rose geometry
const RC = 50, RR = 36;
const rose = (az, r = RR) => {
  const a = (az || 0) * Math.PI / 180;
  return [RC + r * Math.sin(a), RC - r * Math.cos(a)];
};

function trackingAdvice(pass) {
  const el = pass.max_el || 0;
  const rate = pass.angular_velocity_degs || 0;
  const mag = pass.approx_magnitude;
  const optics = passOptics(pass);
  const motion = el >= 60 ? 'Passes near overhead and moves fast, sweep by hand or use an alt-az mount'
    : rate >= 0.8 ? 'Moves quickly, hand-track; sidereal mounts won’t follow it'
    : 'Slow enough to follow by hand';
  return { optics, motion };
}

function Row({ k, dir, detail, color = 'var(--txt-bright)' }) {
  return (
    <div style={{ display:'grid', gridTemplateColumns:'68px 1fr', gap:10, padding:'7px 0', borderBottom:'1px solid var(--hair)', alignItems:'baseline' }}>
      <span style={{ fontFamily:MONO, fontSize: 'var(--ui-small)', letterSpacing:'0.1em', color:'var(--txt-dim)', textTransform:'uppercase' }}>{k}</span>
      <span style={{ fontFamily:'var(--serif)', fontSize: 'var(--ui-body)', color, lineHeight:1.25 }}>
        {dir}
        {detail && <small style={{ display:'block', fontFamily:MONO, fontSize: 'var(--ui-label)', color:'var(--txt-dim)', marginTop:2, letterSpacing:'0.02em' }}>{detail}</small>}
      </span>
    </div>
  );
}

export default function WhereToAim({ pass }) {
  if (!pass) return null;
  const color = tierColor(pass.visibility_tier || 0);
  const appearAz = pass.azimuth_start;
  const peakAz = pass.max_el_az ?? pass.azimuth_max;
  const peakEl = pass.max_el;
  const setAz = pass.azimuth_end;
  const rate = pass.angular_velocity_degs;
  const { optics, motion } = trackingAdvice(pass);

  const [px, py] = rose(peakAz);
  const [ax, ay] = rose(appearAz);
  const [sx, sy] = rose(setAz);

  return (
    <div style={{ paddingBottom: 6 }}>
      <div style={{ display:'flex', alignItems:'baseline', justifyContent:'space-between', marginBottom: 4 }}>
        <span style={{ fontFamily:MONO, fontSize: 'var(--ui-small)', letterSpacing:'0.18em', color:'var(--accent)', textTransform:'uppercase' }}>Where to Aim</span>
        <span style={{ fontFamily:MONO, fontSize: 'var(--ui-small)', color:'var(--txt-mute)', letterSpacing:'0.08em' }}>ALT-AZ · {pass.name}</span>
      </div>

      <div style={{fontSize: 'var(--ui-label)',color:'var(--txt-dim)',lineHeight:1.5}}>{passWindow(pass)}</div>
      <div style={{ display:'flex', gap:14, alignItems:'center', margin:'6px 0 4px' }}>
        {/* compass rose with the pass sweep */}
        <svg width="96" height="96" viewBox="0 0 100 100" style={{ flexShrink:0 }}>
          <circle cx={RC} cy={RC} r={RR} fill="none" stroke="var(--hair-2)" strokeWidth="1" />
          {[['N',0],['E',90],['S',180],['W',270]].map(([l, a]) => {
            const [lx, ly] = rose(a, RR + 7);
            return <text key={l} x={lx} y={ly + 3} textAnchor="middle" fontFamily={MONO} fontSize="8"
                         fill={l === 'N' ? 'var(--txt)' : 'var(--txt-mute)'} fontWeight={l === 'N' ? 700 : 400}>{l}</text>;
          })}
          {/* appear & depart marks on the rim */}
          <circle cx={ax} cy={ay} r="2.4" fill="none" stroke="var(--txt-dim)" strokeWidth="1.2" />
          <circle cx={sx} cy={sy} r="2.4" fill="none" stroke="var(--txt-dim)" strokeWidth="1.2" />
          {/* peak direction arrow */}
          <line x1={RC} y1={RC} x2={px} y2={py} stroke={color} strokeWidth="2" strokeLinecap="round" />
          <circle cx={px} cy={py} r="3.5" fill={color} style={{ filter:`drop-shadow(0 0 4px ${color})` }} />
          <circle cx={RC} cy={RC} r="1.5" fill="var(--txt-mute)" />
        </svg>

        {/* peak az/el readout */}
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontFamily:MONO, fontSize: 'var(--ui-small)', letterSpacing:'0.1em', color:'var(--txt-mute)', textTransform:'uppercase' }}>Point at peak</div>
          <div style={{ fontFamily:'var(--serif)', fontSize:30, color:'var(--txt-bright)', lineHeight:1, marginTop:4 }}>
            {compass(peakAz)}
          </div>
          <div style={{ fontFamily:MONO, fontSize: 'var(--ui-label)', color:'var(--txt-dim)', marginTop:5 }}>
            az {Math.round(peakAz)}° · el {Math.round(peakEl)}°
          </div>
        </div>
      </div>

      <Row k="Appears" dir={compass(appearAz)} detail={`az ${Math.round(appearAz)}° · low on the horizon`} />
      <Row k="Peak" dir={`${compass(peakAz)} · ${Math.round(peakEl)}°`} detail={`az ${Math.round(peakAz)}° · highest point`} color={color} />
      <Row k="Departs" dir={compass(setAz)} detail={`az ${Math.round(setAz)}° · back to the horizon`} />
      <Row k="Motion" dir={`${rate != null ? rate.toFixed(1) : '-'}°/s`} detail={`sweeps ${compass(appearAz)} → ${compass(peakAz)} → ${compass(setAz)}`} />

      <div style={{ marginTop:10, padding:'9px 11px', background:'var(--bg-2)', border:'1px solid var(--hair)', borderRadius:4 }}>
        <div style={{ fontFamily:'var(--sans)', fontSize: 'var(--ui-label)', color:'var(--txt)', lineHeight:1.45 }}>{optics}.</div>
        <div style={{ fontFamily:'var(--sans)', fontSize: 'var(--ui-label)', color:'var(--txt-dim)', lineHeight:1.45, marginTop:3 }}>{motion}.</div>
      </div>
    </div>
  );
}
